import { useCallback, useEffect, useState } from "react";
import { Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FaceScan } from "@/components/FaceScan";
import { getGps } from "@/components/MemberMap";

const Shell = ({ children, testId }) => (
  <div className="min-h-[70vh] grid place-items-center px-4" data-testid={testId}>
    <div className="w-full max-w-sm rounded-3xl border bg-card p-7 text-center shadow-lg">{children}</div>
  </div>
);

const LockedScreen = ({ onLogout }) => {
  const { refresh } = useAuth();
  useEffect(() => { const t = setInterval(refresh, 15000); return () => clearInterval(t); }, [refresh]);
  return (
    <Shell testId="petugas-locked">
      <span className="mx-auto h-16 w-16 rounded-2xl bg-rose-500/12 text-rose-500 grid place-items-center mb-4"><Lock className="h-8 w-8" /></span>
      <p className="font-heading text-xl font-bold">Sistem Sedang Terkunci</p>
      <p className="text-sm text-muted-foreground mt-2">Akun Anda sedang offline. Hubungi Kepala Bagian atau Admin untuk mengaktifkan kembali.</p>
      <Button variant="outline" className="mt-6 h-11 rounded-full w-full" onClick={onLogout} data-testid="locked-logout"><LogOut className="h-4 w-4 mr-2" />Keluar</Button>
    </Shell>
  );
};

const AttendanceScreen = ({ onDone, onLogout }) => {
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(0);
  const submit = async (blob) => {
    setBusy(true);
    try {
      const c = await getGps().catch(() => null);
      if (!c) { toast.error("Aktifkan GPS untuk absensi"); setKey((k) => k + 1); return; }
      const fd = new FormData();
      fd.append("file", blob, "selfie.jpg");
      fd.append("lat", c.lat); fd.append("lng", c.lng);
      const { data } = await api.post("/attendance/checkin", fd);
      toast.success(`Absen masuk ${data.check_in} WIB tercatat`);
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); setKey((k) => k + 1); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center px-1" data-testid="attendance-gate">
      <div className="w-full max-w-sm">
        <FaceScan key={key} onCapture={submit} busy={busy} />
        <Button variant="ghost" className="mt-3 h-10 rounded-full w-full" onClick={onLogout} data-testid="attendance-logout">Keluar</Button>
      </div>
    </div>
  );
};

const ATT_ROLES = ["petugas", "admin_cabang", "kepala_bagian", "kasir", "admin_wilayah"];

export const PetugasGate = ({ children, onLogout }) => {
  const { user } = useAuth();
  const [att, setAtt] = useState(undefined);
  const [failed, setFailed] = useState(false);
  const must = ATT_ROLES.includes(user.role);
  const load = useCallback(() => {
    setFailed(false);
    api.get("/attendance/today").then((r) => setAtt(r.data)).catch(() => setFailed(true));
  }, []);
  useEffect(() => { if (must && (user.role !== "petugas" || user.active)) load(); }, [must, user.role, user.active, load]);
  useEffect(() => { window.__checkedIn = user.role !== "petugas" || Boolean(att); }, [att, user.role]);
  if (!must) return children;
  if (user.role === "petugas" && !user.active) return <LockedScreen onLogout={onLogout} />;
  if (failed) return <div className="text-center py-20"><p className="text-muted-foreground mb-3">Gagal memuat status absensi.</p><Button onClick={load} className="rounded-full" data-testid="attendance-retry">Coba lagi</Button></div>;
  if (att === undefined) return <p className="text-muted-foreground">Memuat...</p>;
  if (!att) return <AttendanceScreen onDone={setAtt} onLogout={onLogout} />;
  return children;
};
